import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { getActiveCallScriptGraph, NO_ACTIVE_SCRIPT_ERROR } from "api/endpoints";
import {
  callscriptGraphToNested,
  type OptimizedCallscriptData,
} from "screens/call/callscript-graph-to-nested";
import type { CallScriptGraph } from "types";

export const useCallScriptGraph = (organisationId: number | null, channelId: number | null) => {
  const [noActiveScript, setNoActiveScript] = useState(false);
  const [graphLoadFailed, setGraphLoadFailed] = useState(false);
  const [graph, setGraph] = useState<CallScriptGraph | null>(null);
  const [processedData, setProcessedData] = useState<OptimizedCallscriptData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchCallScriptGraph = useCallback(async () => {
    if (!(organisationId && channelId)) {
      return;
    }
    setIsLoading(true);
    setNoActiveScript(false);
    setGraphLoadFailed(false);
    setGraph(null);
    setProcessedData(null);

    try {
      const [graphData, graphError] = await getActiveCallScriptGraph(organisationId, channelId);

      if (!graphData || graphError) {
        toast.error(graphError || "Failed to load call script");
        if (graphError === NO_ACTIVE_SCRIPT_ERROR) {
          setNoActiveScript(true);
        } else {
          setGraphLoadFailed(true);
        }
        return;
      }
      setGraph(graphData);

      const processed = callscriptGraphToNested(graphData);
      setProcessedData(processed);
    } catch {
      toast.error("Failed to load call script");
      setGraphLoadFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, [channelId, organisationId]);

  const structuredGraph = useMemo(() => processedData?.nestedStructure ?? null, [processedData]);
  const flattenedGraph = useMemo(() => processedData?.flatStructure ?? null, [processedData]);
  const orderedGroups = useMemo(() => processedData?.orderedGroups ?? [], [processedData]);

  return {
    fetchCallScriptGraph,
    graph,
    isLoading,
    structuredGraph,
    flattenedGraph,
    orderedGroups,
    noActiveScript,
    graphLoadFailed,
  };
};
